import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useDebounce } from 'uikit';

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimIsUniqueContractObjectNameGql } from '../BimContractStructureRequests';

export const useBimUniqueContractObject = ({
    contractId,
    constructionTypeId,
    name,
    isDirtyObjectName,
}: {
    contractId: number | undefined;
    constructionTypeId: number | undefined;
    name: string;
    isDirtyObjectName?: boolean;
}) => {
    const dispatch = useDispatch();

    const variables = useMemo(() => {
        if (constructionTypeId && name?.length) {
            return { contractId, constructionTypeId, name: name?.trim() };
        }

        return undefined;
    }, [constructionTypeId, contractId, name]);

    const [
        bimUniqueContractObjectData,
        bimUniqueContractObjectStatus,
        bimUniqueContractObjectError,
        bimUniqueContractObjectRequest,
    ] = useRequest({
        requestId: 'bimIsUniqueContractObjectName',
        query: bimIsUniqueContractObjectNameGql,
        withEffect: false,
    });

    const data = useMemo(
        () => bimUniqueContractObjectData?.bimIsUniqueContractObjectName,
        [bimUniqueContractObjectData?.bimIsUniqueContractObjectName],
    );

    const debounceRequest = useDebounce(() => {
        if (name && isDirtyObjectName) {
            dispatch(bimUniqueContractObjectRequest(variables));
        }
    }, 300);

    const [resetBimIsUniqueContractObjectName] = useResetStoreData({ requestId: 'bimIsUniqueContractObjectName' });

    useEffect(() => {
        if (name?.length) {
            debounceRequest();
        }
    }, [debounceRequest, name?.length]);

    return {
        bimUniqueContractObjectData: data,
        bimUniqueContractObjectStatus,
        bimUniqueContractObjectError,
        bimUniqueContractObjectRequest,
        resetBimIsUniqueContractObjectName,
    };
};
